import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Package } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from '../i18n/LocaleContext';
import API_CONFIG from '../config/api';
import taskService from '../services/taskService';
import TaskDetailSheet from '../components/TaskDetailSheet';
import Toast from '../components/Toast';
import HistoryRowsSkeleton from '../components/HistoryRowsSkeleton';
import { getPlatformMeta, getActionMeta } from '../data/taskMeta';

const DATE_LOCALES = { zh: 'zh-CN', ko: 'ko-KR', ja: 'ja-JP', en: 'en-US' };

// History timeline: full-bleed list rows, uniform density, square covers, flat status chips.
const HistoryPage = () => {
  const { t, language } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [toast, setToast] = useState(null);
  const loaderRef = useRef(null);

  const formatCurrency = (amount) => {
    const value = amount || 0;
    const isWholeNumber = value % 1 === 0;
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: isWholeNumber ? 0 : 2,
      maximumFractionDigits: isWholeNumber ? 0 : 2
    }).format(value);
    return `$${formatted}`;
  };

  const loadHistory = useCallback(async (targetPage) => {
    if (loading || (!hasMore && targetPage > 1)) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(API_CONFIG.getApiUrl(`/user/orders/history?page=${targetPage}&limit=100`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders((previous) => targetPage === 1 ? response.data.orders : [...previous, ...response.data.orders]);
      setHasMore(response.data.pagination.has_more);
      setPage(targetPage);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore]);

  useEffect(() => { loadHistory(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTasks = useCallback(() => {
    return taskService.getMine(1, 100)
      .then((res) => setTasks(res.tasks || []))
      .catch((error) => console.error('Fetch task history error:', error));
  }, []);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && hasMore && !loading) loadHistory(page + 1);
    }, { rootMargin: '200px' });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [page, hasMore, loading, loadHistory]);

  // One timeline: prepaid orders (tb_history) and social tasks, newest first
  const feed = useMemo(() => {
    const fromOrders = orders.map((order) => ({
      key: `o-${order.id}`,
      kind: 'order',
      title: order.product_name,
      image: order.product_image,
      time: order.completed_at || order.created_at,
      commission: Number(order.commission),
      status: 'approved',
    }));
    const fromTasks = tasks.map((task) => ({
      key: `t-${task.id}`,
      kind: 'task',
      title: task.title,
      image: task.image_url,
      time: task.reviewed_at || task.submitted_at || task.claimed_at,
      commission: Number(task.reward),
      status: task.status,
      platform: task.platform,
      actionType: task.action_type,
      // Shape TaskDetailSheet expects (same as the feed API)
      sheetTask: {
        id: task.pool_task_id,
        platform: task.platform,
        actionType: task.action_type,
        title: task.title,
        description: task.description,
        imageUrl: task.image_url,
        videoUrl: task.video_url || null,
        targetUrl: task.target_url,
        reward: Number(task.reward),
        rejectReason: task.reject_reason,
        reviewedAt: task.reviewed_at,
        openTask: (task.status === 'claimed' || task.status === 'pending_review')
          ? { status: task.status, expireAt: task.expire_at, proofUrl: task.proof_url }
          : null,
        finalStatus: task.status === 'approved' || task.status === 'rejected' ? task.status : null,
      },
    }));
    return [...fromOrders, ...fromTasks].sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
  }, [orders, tasks]);

  const dateLocale = DATE_LOCALES[language] || 'vi-VN';

  const statusChip = (item) => {
    if (item.status === 'approved') {
      return { label: `+${formatCurrency(item.commission)}`, cls: 'bg-money/10 text-money-dark' };
    }
    if (item.status === 'pending_review') return { label: t('taskReviewing'), cls: 'bg-canvas text-ink-secondary' };
    if (item.status === 'rejected') return { label: t('taskRejected'), cls: 'bg-red-50 text-red-500' };
    return { label: t('taskInProgress'), cls: 'bg-canvas text-ink-secondary' };
  };

  return (
    <div className="pb-20 bg-canvas min-h-screen">
      <div className="sticky top-0 z-20 bg-white">
        <div className="max-w-md mx-auto px-4 pt-4 pb-1">
          <h2 className="text-[19px] font-bold text-ink tracking-tight">{t('orderHistory')}</h2>
          <p className="mt-1 text-[12.5px] text-ink-muted">{t('historySubtitle')}</p>
        </div>
      </div>

      <div className="max-w-md mx-auto">
        <div className="bg-white border-y border-black/[0.05] divide-y divide-black/[0.05]">
          {feed.length === 0 && loading ? (
            <HistoryRowsSkeleton />
          ) : feed.map((item) => {
            const RowIcon = item.kind === 'task' ? getActionMeta(item.actionType).Icon : Package;
            const source = item.kind === 'task' ? getPlatformMeta(item.platform).label : t('orderPrefix');
            const chip = statusChip(item);
            const Wrapper = item.kind === 'task' ? 'button' : 'article';
            return (
              <Wrapper
                key={item.key}
                type={item.kind === 'task' ? 'button' : undefined}
                onClick={item.kind === 'task' ? () => setSelectedTask(item.sheetTask) : undefined}
                className={`w-full flex gap-3 px-4 py-3.5 text-left ${
                  item.kind === 'task' ? 'active:bg-canvas transition-colors' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-[12px] text-ink-muted">
                    <span className="w-5 h-5 shrink-0 rounded-[6px] bg-canvas flex items-center justify-center">
                      <RowIcon className="w-3 h-3 text-ink-secondary" />
                    </span>
                    <span className="truncate capitalize">{source}</span>
                  </p>
                  <h3 className="mt-2 text-[14.5px] font-bold text-ink leading-snug line-clamp-2 break-words">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-[11.5px] text-ink-muted tabular">
                    {item.time ? new Date(item.time).toLocaleString(dateLocale) : ''}
                  </p>
                </div>
                <div className="shrink-0 flex flex-col items-end justify-between">
                  <span className="w-[60px] h-[60px] rounded-lg overflow-hidden ring-1 ring-black/[0.06]">
                    <img
                      src={API_CONFIG.getImageUrl(item.image) || '/images/placeholder.svg'}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
                    />
                  </span>
                  <span className={`mt-2 px-2 py-0.5 rounded text-[12px] font-bold tabular ${chip.cls}`}>
                    {chip.label}
                  </span>
                </div>
              </Wrapper>
            );
          })}
        </div>
        <div ref={loaderRef} className="py-6 text-center text-sm text-ink-muted">
          {loading ? t('loadingHistory') : hasMore ? t('loadMore') : feed.length > 0 ? t('allHistoryShown') : t('noOrderHistory')}
        </div>
      </div>

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      {selectedTask && (
        <TaskDetailSheet
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onChanged={async () => { await loadTasks(); setSelectedTask(null); }}
          onError={(message) => setToast({ type: 'error', message })}
        />
      )}
    </div>
  );
};

export default HistoryPage;
